import { PageRoutes } from '@enums'
import { IMeta } from '@store'
import { ICampaign } from '@store/campaigns'
import { ISubmission } from '@store/submissions'

export const buildSubmissionUrl = (campaign: ICampaign, submission: ISubmission) =>
  `/${PageRoutes.Campaigns}/${campaign.id}/${PageRoutes.Submissions}/${submission.id}`

export const buildSubmissionUrlById = (campaignId: number, submissionId?: number) =>
  submissionId && `/${PageRoutes.Campaigns}/${campaignId}/${PageRoutes.Submissions}/${submissionId}`

export const isActiveMenu = (
  currentPath: string,
  targetPath: string,
  currentFilter?: string | number,
  targetFilter?: string | number,
) => {
  return currentPath.includes(targetPath) && currentFilter === targetFilter
}

const updateQueryParamEntry = (
  link: string,
  queryParam: string,
  replaceableToken: string,
  filterValue?: string,
) => {
  if (filterValue) {
    return link.replace(replaceableToken, filterValue)
  }
  return link.replace(queryParam, '')
}

export const sanitizeQueryParams = (link: string, { filter, sortBy, declinedFilter }: IMeta) => {
  let sanitizedLink = link.replace('{filter}', filter as string)
  sanitizedLink = updateQueryParamEntry(sanitizedLink, '&sort_by={sort_by}', '{sort_by}', sortBy)
  sanitizedLink = updateQueryParamEntry(
    sanitizedLink,
    '&declined_filter={declined_filter}',
    '{declined_filter}',
    declinedFilter,
  )
  return sanitizedLink
}

/**
 * Validates if a URL string is a valid HTTPS URL with a proper domain and TLD
 * @param value - The URL string to validate
 * @param requireHttps - Whether to require HTTPS protocol (default: true)
 * @returns true if invalid, false if valid
 */
export const isInvalidUrl = (value: string, requireHttps = true): boolean => {
  if (!value) return false // Empty is valid (handled by required validation elsewhere)

  try {
    const url = new URL(value)

    // Check protocol if required
    if (requireHttps && url.protocol !== 'https:') return true

    // Validate hostname has at least 2 non-empty parts (e.g., 'example.com')
    // This prevents invalid hostnames like 'test.', '.test', '..com', or just numbers like '123'
    const hostnameParts = url.hostname.split('.')
    const hasValidTLD = hostnameParts.length >= 2 && hostnameParts.every((part) => part.length > 0)
    // Also reject if hostname is all numeric (like '123' or IP addresses without dots)
    const isNumericOnly = /^\d+$/.test(url.hostname)

    return !hasValidTLD || isNumericOnly
  } catch {
    return true // Invalid URL format
  }
}

/**
 * Normalizes a URL by prepending https:// if missing protocol,
 * or upgrading http:// to https://
 * @param url - The URL string to normalize
 * @returns Normalized URL string with https:// protocol
 */
export const normalizeUrl = (url: string): string => {
  if (!url) return url
  const trimmed = url.trim()

  // No normalization needed - URL already uses secure https:// protocol
  if (trimmed.startsWith('https://')) return trimmed

  // Has http:// - upgrade to https://
  if (trimmed.startsWith('http://')) {
    return trimmed.replace(/^http:\/\//, 'https://')
  }

  // Has other protocol (ftp://, etc.) - don't modify
  if (trimmed.includes('://')) return trimmed

  // No protocol - prepend https://
  return `https://${trimmed}`
}
